import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Package, Loader2, Clock, AlertTriangle } from 'lucide-react';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import DocumentHead from '@/components/DocumentHead';
import JsonLd from '@/components/JsonLd';
import BrandSymbol from '@/components/BrandSymbol';
import { SITE_NAME, SITE_URL } from '@/config/site';
import { useState, useEffect } from 'react';

function useCountdown(endsAt: string | null) {
  const { t } = useTranslation('bundles');
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!endsAt) return;

    function update() {
      const diff = new Date(endsAt!).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft(t('detail.expired')); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      if (days > 0) setTimeLeft(t('detail.daysHoursLeft', { days, hours }));
      else if (hours > 0) setTimeLeft(t('detail.hoursMinutesLeft', { hours, minutes }));
      else setTimeLeft(t('detail.minutesLeft', { minutes }));
    }

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [endsAt, t]);

  return timeLeft;
}

export default function BundleDetail() {
  const { t } = useTranslation('bundles');
  const { slug } = useParams<{ slug: string }>();
  const { data: bundle, isLoading } = useQuery({
    queryKey: ['bundle', slug],
    queryFn: () =>
      api.get<any>(`/bundles/${slug}`),
    enabled: !!slug,
  });

  const addBundle = useCartStore((s) => s.addBundle);
  const addToast = useUIStore((s) => s.addToast);
  const [isAdding, setIsAdding] = useState(false);
  const countdown = useCountdown(bundle?.ends_at || null);
  const expiredLabel = t('detail.expired');

  const [variantSelections, setVariantSelections] = useState<Record<string, string>>({});

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 flex justify-center">
        <Loader2
          className="h-8 w-8 animate-spin text-sage-600"
          aria-label={t('detail.loadingAriaLabel')}
        />
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-2xl font-bold text-stone-900">{t('detail.notFound')}</h1>
        <Link to="/bundles" className="mt-4 inline-block text-sage-700 hover:text-sage-800">
          {t('detail.viewAll')}
        </Link>
      </div>
    );
  }

  const items = bundle.items || [];
  const totalPrice = bundle.total_price || 0;
  const discountedPrice = bundle.discounted_price || totalPrice;
  const savings = totalPrice - discountedPrice;
  const stockRemaining = bundle.stock_limit != null ? Math.max(0, bundle.stock_limit - (bundle.stock_sold || 0)) : null;
  const isSoldOut = stockRemaining !== null && stockRemaining <= 0;

  const itemsNeedingSelection = items.filter((item: any) =>
    !item.variant_id && item.available_variants && item.available_variants.length > 1
  );
  const allVariantsSelected = itemsNeedingSelection.every(
    (item: any) => variantSelections[item.product_id]
  );
  const canAddToCart = !isSoldOut && (itemsNeedingSelection.length === 0 || allVariantsSelected);

  async function handleAddBundle() {
    setIsAdding(true);
    try {
      const selections = Object.entries(variantSelections).map(([product_id, variant_id]) => ({
        product_id,
        variant_id,
      }));
      await addBundle(bundle.id, selections.length > 0 ? selections : undefined);
      addToast(t('detail.addedToast'), 'success');
    } catch {
      addToast(t('detail.addFailedToast'), 'error');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <DocumentHead
        title={bundle.name}
        description={bundle.description || t('detail.fallbackDescription', { name: bundle.name, site: SITE_NAME })}
        ogImage={bundle.image_url}
        ogType="product"
        canonicalUrl={`/bundles/${bundle.slug}`}
      />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: bundle.name,
        description: bundle.description || t('detail.fallbackJsonDescription', { name: bundle.name }),
        ...(bundle.image_url ? { image: [bundle.image_url] } : {}),
        brand: { '@type': 'Brand', name: SITE_NAME },
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: discountedPrice.toFixed(2),
          highPrice: totalPrice.toFixed(2),
          priceCurrency: 'USD',
          offerCount: items.length,
          availability: isSoldOut ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
        },
      }} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('detail.breadcrumbs.home'), item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: t('detail.breadcrumbs.bundles'), item: `${SITE_URL}/bundles` },
          { '@type': 'ListItem', position: 3, name: bundle.name },
        ],
      }} />

      <nav className="text-sm text-stone-500 mb-6" aria-label={t('detail.breadcrumbs.label')}>
        <Link to="/bundles" className="hover:text-sage-700">{t('detail.breadcrumbs.bundles')}</Link>
        <span className="mx-2">/</span>
        <span className="text-stone-900">{bundle.name}</span>
      </nav>

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        {bundle.image_url && (
          <img src={bundle.image_url} alt={bundle.name} className="w-full h-64 object-cover" />
        )}

        <div className="p-6">
          <h1 className="text-3xl font-display font-bold text-stone-900">{bundle.name}</h1>
          {bundle.description && (
            <p className="mt-2 text-stone-600">{bundle.description}</p>
          )}

          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-3xl font-bold text-sage-700">{formatCurrency(discountedPrice)}</span>
            {savings > 0 && (
              <>
                <span className="text-lg text-stone-400 line-through">{formatCurrency(totalPrice)}</span>
                <span className="text-sm font-semibold text-sage-600 bg-sage-50 px-2 py-0.5 rounded">
                  {t('detail.save', { amount: formatCurrency(savings) })}
                </span>
              </>
            )}
          </div>

          {(countdown || stockRemaining !== null) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {countdown && countdown !== expiredLabel && (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  {countdown}
                </span>
              )}
              {stockRemaining !== null && !isSoldOut && (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-orange-700 bg-orange-50 px-2.5 py-1 rounded-full">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('detail.remaining', { count: stockRemaining })}
                </span>
              )}
              {isSoldOut && (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-red-700 bg-red-50 px-2.5 py-1 rounded-full">
                  {t('detail.soldOut')}
                </span>
              )}
            </div>
          )}

          <div className="mt-6">
            <Button size="lg" onClick={handleAddBundle} isLoading={isAdding} disabled={!canAddToCart}>
              <ShoppingCart className="h-5 w-5" aria-hidden="true" />
              {isSoldOut
                ? t('detail.soldOut')
                : !allVariantsSelected && itemsNeedingSelection.length > 0
                ? t('detail.selectOptionsBelow')
                : t('detail.addBundleToCart')}
            </Button>
          </div>
        </div>
      </div>

      {/* Included products */}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-stone-900 mb-4">
          {t('detail.whatsIncluded', { count: items.length })}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((item: any) => {
            const needsSelection = !item.variant_id && item.available_variants && item.available_variants.length > 1;
            return (
              <div
                key={item.product_id}
                className="flex gap-4 p-4 bg-white rounded-lg border border-stone-200"
              >
                <Link to={`/product/${item.product_slug}`} className="w-16 h-16 rounded-md overflow-hidden bg-stone-100 shrink-0">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BrandSymbol className="h-6 w-6 text-stone-300" />
                    </div>
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.product_slug}`} className="font-medium text-stone-900 hover:text-sage-700">
                    {item.product_name}
                  </Link>
                  <p className="text-sm text-stone-500">
                    {t('detail.qty', { count: item.quantity || 1 })}
                  </p>
                  <p className="text-sm font-semibold text-sage-700">{formatCurrency(Number(item.price))}</p>
                  {needsSelection && (
                    <select
                      value={variantSelections[item.product_id] || ''}
                      onChange={(e) => setVariantSelections(prev => ({ ...prev, [item.product_id]: e.target.value }))}
                      className="mt-2 w-full text-sm rounded-lg border border-stone-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-sage-500"
                      aria-label={t('detail.selectOptionAriaLabel', { name: item.product_name })}
                    >
                      <option value="">{t('detail.chooseOption')}</option>
                      {item.available_variants.map((v: any) => (
                        <option key={v.id} value={v.id}>
                          {v.name} — {formatCurrency(Number(v.price))}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
